import React from 'react';
import { X, Star, SlidersHorizontal } from 'lucide-react';
import { Input } from './index';

const FilterBar = ({
  selectedPriceRange,
  setSelectedPriceRange,
  selectedRating,
  setSelectedRating,
  showMoreFilters,
  setShowMoreFilters,
  // Vendor-specific filters
  searchTerm = '',
  setSearchTerm,
  location = '',
  setLocation,
  selectedCategory = '',
  setSelectedCategory,
  categories = [],
  sortBy = 'none',
  setSortBy,
  sortOptions = [],
  clearAllFilters,
  showCategoryFilter = true, // Hide on CategoryServices page
  priceMin = 1,
  setPriceMin,
  priceMax = 1000,
  setPriceMax
}) => {
  const priceRanges = [
    { value: 'all', label: 'All Prices' },
    { value: '0-500', label: 'Under $500' },
    { value: '500-1000', label: '$500 - $1000' },
    { value: '1000-2000', label: '$1000 - $2000' },
    { value: '2000+', label: '$2000+' }
  ];

  const ratingOptions = [
    { value: 'all', label: 'All Ratings' },
    { value: '4.5+', label: '4.5+ Stars' },
    { value: '4.0+', label: '4.0+ Stars' }
  ];

  // Get active filters count
  const activeFiltersCount = [
    selectedPriceRange !== 'all',
    selectedRating !== 'all'
  ].filter(Boolean).length;

  return (
    <>
      {/* All Filters Button */}
        <button
        onClick={() => setShowMoreFilters(true)}
          className={`px-4 md:px-4 py-2 md:py-2 rounded-full border transition-all whitespace-nowrap ${
          activeFiltersCount > 0
            ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white text-gray-700'
        }`}
        >
          <div className="flex items-center gap-2 text-xs md:text-sm font-light">
            <SlidersHorizontal className="w-3 h-3 md:w-4 md:h-4" />
          <span className="hidden sm:inline">All Filters</span>
            <span className="sm:hidden">Filters</span>
            {activeFiltersCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-white text-gray-900 rounded-full text-xs font-medium">
                {activeFiltersCount}
              </span>
            )}
          </div>
        </button>

      {/* All Filters Modal */}
      {showMoreFilters && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
          onClick={() => setShowMoreFilters(false)}
        >
          <div 
            className="bg-white rounded-t-3xl md:rounded-3xl shadow-xl w-full md:max-w-2xl md:w-full max-h-[85vh] md:max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 md:px-6 py-5 flex items-center justify-between z-10 rounded-t-3xl">
              <h3 className="text-lg md:text-xl font-light text-gray-900">All Filters</h3>
              <button
                onClick={() => setShowMoreFilters(false)}
                className="p-2 hover:bg-gray-50 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-6 md:space-y-8">
              {/* Search & Location Section - Only for vendors */}
              {(setSearchTerm || setLocation) && (
                <div className="space-y-5">
                  {setSearchTerm && (
                    <div>
                      <h4 className="text-sm font-light text-gray-600 mb-3 uppercase tracking-wide">Search</h4>
                      <Input
                        type="text"
                        placeholder="Search vendors or services..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        icon="search"
                      />
                    </div>
                  )}

                  {setLocation && (
                    <div>
                      <h4 className="text-sm font-light text-gray-600 mb-3 uppercase tracking-wide">Location</h4>
                      <Input
                        type="text"
                        placeholder="Enter location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        icon="location"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Category Section - Hide on CategoryServices */}
              {showCategoryFilter && categories.length > 0 && setSelectedCategory && (
                <div>
                  <h4 className="text-sm font-light text-gray-600 mb-4 uppercase tracking-wide">Category</h4>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {categories.map((category) => (
                      <label
                        key={category.value}
                        className="flex items-center justify-between px-4 py-2.5 h-[42px] rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <span className="text-sm font-light text-gray-700">{category.label}</span>
                        <input
                          type="checkbox"
                          checked={selectedCategory === category.value}
                          onChange={() => setSelectedCategory(selectedCategory === category.value ? '' : category.value)}
                          className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500 flex-shrink-0"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Range Section */}
              <div>
                <h4 className="text-sm font-light text-gray-600 mb-4 uppercase tracking-wide">Price Range</h4>
                {setPriceMin && setPriceMax ? (
                  // Custom price inputs for vendors
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-light text-gray-500 mb-2">Min Price ($)</label>
                      <Input
                        type="number"
                        placeholder="Min"
                        value={priceMin}
                        onChange={(e) => setPriceMin(Math.max(1, parseInt(e.target.value) || 1))}
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-light text-gray-500 mb-2">Max Price ($)</label>
                      <Input
                        type="number"
                        placeholder="Max"
                        value={priceMax}
                        onChange={(e) => setPriceMax(Math.max(1, parseInt(e.target.value) || 1000))}
                        min="1"
                      />
                    </div>
                  </div>
                ) : (
                  // Price ranges for services
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {priceRanges.map((range) => (
                    <label
                      key={range.value}
                      className={`flex items-center justify-center px-3 py-2 h-[42px] rounded-lg cursor-pointer transition-all ${
                        selectedPriceRange === range.value
                          ? 'bg-gray-900 text-white border border-gray-900'
                          : 'border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm font-light">{range.label}</span>
                      <input
                        type="radio"
                        name="priceRangeModal"
                        value={range.value}
                        checked={selectedPriceRange === range.value}
                        onChange={(e) => setSelectedPriceRange(e.target.value)}
                        className="sr-only"
                      />
                    </label>
                  ))}
                </div>
                )}
              </div>

              {/* Rating Section */}
              <div>
                <h4 className="text-sm font-light text-gray-600 mb-4 uppercase tracking-wide">Minimum Rating</h4>
                <div className="grid grid-cols-1 gap-2">
                  {ratingOptions.map((rating) => (
                    <label
                      key={rating.value}
                      className={`flex items-center justify-between px-4 py-2.5 h-[42px] rounded-lg cursor-pointer transition-all ${
                        selectedRating === rating.value
                          ? 'bg-gray-900 text-white border border-gray-900'
                          : 'border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Star className={`w-4 h-4 fill-current flex-shrink-0 ${
                          selectedRating === rating.value ? 'text-white' : 'text-gray-400'
                        }`} />
                        <span className="text-sm font-light">{rating.label}</span>
                      </div>
                      <input
                        type="radio"
                        name="ratingModal"
                        value={rating.value}
                        checked={selectedRating === rating.value}
                        onChange={(e) => setSelectedRating(e.target.value)}
                        className="w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-500 flex-shrink-0"
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Sort By - Only for vendors */}
              {setSortBy && sortOptions.length > 0 && (
                <div>
                  <h4 className="text-sm font-light text-gray-600 mb-4 uppercase tracking-wide">Sort By</h4>
                  <div className="space-y-2">
                    {sortOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center justify-between px-4 py-2.5 h-[42px] rounded-lg cursor-pointer transition-all ${
                          sortBy === option.value
                            ? 'bg-gray-900 text-white border border-gray-900'
                            : 'border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-sm font-light">{option.label}</span>
                        <input
                          type="radio"
                          name="sortBy"
                          value={option.value}
                          checked={sortBy === option.value}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-500 flex-shrink-0"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Filters */}
              <div>
                <h4 className="text-sm font-light text-gray-600 mb-4 uppercase tracking-wide">Additional Options</h4>
                <div className="space-y-2">
                  <label className="flex items-center justify-between px-4 py-2.5 h-[42px] rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-all">
                    <span className="text-sm font-light text-gray-700">Featured only</span>
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500 flex-shrink-0"
                    />
                  </label>
                  <label className="flex items-center justify-between px-4 py-2.5 h-[42px] rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-all">
                    <span className="text-sm font-light text-gray-700">Instant booking available</span>
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500 flex-shrink-0"
                    />
                  </label>
                  <label className="flex items-center justify-between px-4 py-2.5 h-[42px] rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-all">
                    <span className="text-sm font-light text-gray-700">Free cancellation</span>
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-500 flex-shrink-0"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t border-gray-100 px-4 md:px-6 py-5 flex gap-3">
              <button
                onClick={() => {
                  if (clearAllFilters) {
                    clearAllFilters();
                  } else {
                  setSelectedPriceRange('all');
                  setSelectedRating('all');
                  }
                }}
                className="flex-1 py-3 border border-gray-300 text-gray-600 rounded-lg font-light hover:border-gray-400 hover:bg-gray-50 transition-all text-sm md:text-base"
              >
                Clear all
              </button>
              <button
                onClick={() => setShowMoreFilters(false)}
                className="flex-1 py-3 bg-gray-900 text-white rounded-lg font-light hover:bg-gray-800 transition-all text-sm md:text-base"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterBar;

